// Onboarding "What do you want to improve?": life areas → sub-areas → suggested
// habits. Shared by web + native onboarding and the backend (validation, coach
// context). Keys are stable: they're saved to userPreferences.
// Pure TS.

import type { LifeCategory } from './habitCategory'
import {
  FAST_PRESETS,
  type FastPresetId,
  type HabitMeasure,
  MEASURE_PRESETS,
  type MeasurePresetId,
  fastMeasure,
} from './habitMeasure'
import type { RecursPeriod } from './schedule'

export const IMPROVE_LANGS = ['en', 'es', 'fr', 'ja', 'pt', 'zh'] as const
export type ImproveLang = (typeof IMPROVE_LANGS)[number]
export type L10n = Record<ImproveLang, string>

const l = (en: string, es: string, fr: string, ja: string, pt: string, zh: string): L10n => ({
  en,
  es,
  fr,
  ja,
  pt,
  zh,
})

export function improveLang(lang: string | null | undefined): ImproveLang {
  const two = (lang ?? 'en').slice(0, 2).toLowerCase()
  return (IMPROVE_LANGS as readonly string[]).includes(two) ? (two as ImproveLang) : 'en'
}

// ─── suggestions ─────────────────────────────────────────────────────────────

type SuggestionMeasure =
  | { preset: MeasurePresetId }
  | { fast: FastPresetId }
  | { custom: Omit<HabitMeasure, 'unit'>; unit: L10n }

export type ImproveSuggestion = {
  title: L10n
  icon: string
  category: LifeCategory
  period: RecursPeriod
  // Only with period 'days': 0 = Sun … 6 = Sat.
  days?: number[]
  measure?: SuggestionMeasure
}

// Preset units as the clients' numberHabit.presets.<id>.unit / fasting.unit
// strings, so a suggested preset matches one the composer would make.
const PRESET_UNITS: Partial<Record<MeasurePresetId, L10n>> = {
  steps: l('steps', 'pasos', 'pas', '歩', 'passos', '步'),
  water: l('glasses', 'vasos', 'verres', '杯', 'copos', '杯'),
  pages: l('pages', 'páginas', 'pages', 'ページ', 'páginas', '页'),
  meals: l('meals', 'comidas', 'repas', '食', 'refeições', '餐'),
}
const FAST_UNIT = l('h', 'h', 'h', '時間', 'h', '小时')

const daily = 'daily' as const
const weekly = 'weekly' as const
const monthly = 'monthly' as const
const weekday = 'weekday' as const

export const IMPROVE_SUGGESTIONS = {
  // health
  jog: {
    title: l('Go for a run', 'Salir a correr', 'Aller courir', 'ランニング', 'Correr', '跑步'),
    icon: 'footprints',
    category: 'health',
    period: 'days',
    days: [2, 4, 6],
  },
  steps: {
    title: l('Steps', 'Pasos', 'Pas', '歩数', 'Passos', '步数'),
    icon: 'footprints',
    category: 'health',
    period: daily,
    measure: { preset: 'steps' },
  },
  stairs: {
    title: l(
      'Take the stairs',
      'Usar las escaleras',
      'Prendre l’escalier',
      '階段を使う',
      'Usar as escadas',
      '走楼梯',
    ),
    icon: 'footprints',
    category: 'health',
    period: daily,
  },
  lift: {
    title: l(
      'Strength training',
      'Entrenamiento de fuerza',
      'Musculation',
      '筋トレ',
      'Treino de força',
      '力量训练',
    ),
    icon: 'dumbbell',
    category: 'health',
    period: 'days',
    days: [1, 3, 5],
  },
  meals: {
    title: l('Meals', 'Comidas', 'Repas', '食事', 'Refeições', '餐数'),
    icon: 'utensils',
    category: 'health',
    period: daily,
    measure: { preset: 'meals' },
  },
  protein: {
    title: l('Protein', 'Proteína', 'Protéines', 'タンパク質', 'Proteína', '蛋白质'),
    icon: 'salad',
    category: 'health',
    period: daily,
    measure: {
      custom: { direction: 'atLeast', target: 120, quickAdds: [20, 30] },
      unit: l('g', 'g', 'g', 'g', 'g', '克'),
    },
  },
  sleep8: {
    title: l(
      'Sleep 8 hours',
      'Dormir 8 horas',
      'Dormir 8 heures',
      '8時間眠る',
      'Dormir 8 horas',
      '睡够8小时',
    ),
    icon: 'bed-double',
    category: 'health',
    period: daily,
  },
  water: {
    title: l('Drink water', 'Beber agua', 'Boire de l’eau', '水を飲む', 'Beber água', '喝水'),
    icon: 'droplet',
    category: 'health',
    period: daily,
    measure: { preset: 'water' },
  },
  veggies: {
    title: l(
      'Eat vegetables with two meals',
      'Comer verdura en dos comidas',
      'Des légumes à deux repas',
      '2食で野菜を食べる',
      'Comer legumes em duas refeições',
      '两餐吃蔬菜',
    ),
    icon: 'carrot',
    category: 'health',
    period: daily,
  },
  noLateSnacks: {
    title: l(
      'No snacks after dinner',
      'Nada de picar después de cenar',
      'Pas de grignotage après le dîner',
      '夕食後は間食しない',
      'Sem lanches depois do jantar',
      '晚饭后不吃零食',
    ),
    icon: 'moon',
    category: 'health',
    period: daily,
  },
  bedtime: {
    title: l(
      'Lights out at the same time',
      'Apagar la luz a la misma hora',
      'Extinction des feux à heure fixe',
      '毎日同じ時間に寝る',
      'Apagar a luz no mesmo horário',
      '固定时间熄灯',
    ),
    icon: 'moon',
    category: 'health',
    period: daily,
  },
  noScreensBed: {
    title: l(
      'No screens 30 min before bed',
      'Sin pantallas 30 min antes de dormir',
      'Pas d’écran 30 min avant de dormir',
      '寝る30分前はスマホを見ない',
      'Sem telas 30 min antes de dormir',
      '睡前30分钟不看屏幕',
    ),
    icon: 'phone-off',
    category: 'health',
    period: daily,
  },
  cookHome: {
    title: l(
      'Cook at home',
      'Cocinar en casa',
      'Cuisiner maison',
      '自炊する',
      'Cozinhar em casa',
      '在家做饭',
    ),
    icon: 'utensils',
    category: 'health',
    period: daily,
  },
  stretch: {
    title: l(
      'Stretch for 10 minutes',
      'Estirar 10 minutos',
      'S’étirer 10 minutes',
      '10分ストレッチ',
      'Alongar por 10 minutos',
      '拉伸10分钟',
    ),
    icon: 'person-standing',
    category: 'health',
    period: daily,
  },
  yoga: {
    title: l('Yoga session', 'Sesión de yoga', 'Séance de yoga', 'ヨガ', 'Sessão de yoga', '瑜伽'),
    icon: 'person-standing',
    category: 'health',
    period: 'days',
    days: [2, 6],
  },
  smokeFree: {
    title: l(
      'Smoke-free day',
      'Día sin fumar',
      'Journée sans tabac',
      '吸わない一日',
      'Dia sem fumar',
      '无烟日',
    ),
    icon: 'cigarette-off',
    category: 'health',
    period: daily,
  },
  cravingBreaths: {
    title: l(
      'Breathe through a craving',
      'Respirar cuando llegue el antojo',
      'Respirer face à une envie',
      '吸いたくなったら深呼吸',
      'Respirar quando bater a vontade',
      '想抽时深呼吸',
    ),
    icon: 'wind',
    category: 'health',
    period: daily,
  },
  dryWeekdays: {
    title: l(
      'Alcohol-free on weekdays',
      'Sin alcohol entre semana',
      'Sans alcool en semaine',
      '平日はお酒を飲まない',
      'Sem álcool durante a semana',
      '工作日不喝酒',
    ),
    icon: 'wine-off',
    category: 'health',
    period: weekday,
  },
  alcoholFree: {
    title: l(
      'Alcohol-free day',
      'Día sin alcohol',
      'Journée sans alcool',
      '休肝日',
      'Dia sem álcool',
      '无酒日',
    ),
    icon: 'wine-off',
    category: 'health',
    period: daily,
  },
  noSugaryDrinks: {
    title: l(
      'No sugary drinks',
      'Sin bebidas azucaradas',
      'Pas de boissons sucrées',
      '甘い飲み物を飲まない',
      'Sem bebidas açucaradas',
      '不喝含糖饮料',
    ),
    icon: 'droplet',
    category: 'health',
    period: daily,
  },
  noAddedSugar: {
    title: l(
      'No added sugar',
      'Sin azúcar añadido',
      'Pas de sucre ajouté',
      '砂糖を控える',
      'Sem açúcar adicionado',
      '不吃添加糖',
    ),
    icon: 'apple',
    category: 'health',
    period: daily,
  },
  fast16: {
    title: l('16:8 fast', 'Ayuno 16:8', 'Jeûne 16:8', '16:8 断食', 'Jejum 16:8', '16:8 断食'),
    icon: 'hourglass',
    category: 'health',
    period: daily,
    measure: { fast: 'fast16' },
  },
  // career
  deepWork: {
    title: l(
      '90 minutes of deep work',
      '90 minutos de trabajo profundo',
      '90 minutes de travail concentré',
      '90分の集中作業',
      '90 minutos de trabalho focado',
      '90分钟深度工作',
    ),
    icon: 'brain',
    category: 'career',
    period: weekday,
  },
  phoneAway: {
    title: l(
      'Phone in another room while working',
      'El móvil en otra habitación al trabajar',
      'Téléphone dans une autre pièce en travaillant',
      '作業中はスマホを別の部屋に',
      'Celular em outro cômodo ao trabalhar',
      '工作时手机放在另一个房间',
    ),
    icon: 'phone-off',
    category: 'career',
    period: weekday,
  },
  top3: {
    title: l(
      'Plan tomorrow’s top 3',
      'Planear las 3 prioridades de mañana',
      'Prévoir les 3 priorités de demain',
      '明日のトップ3を決める',
      'Planejar as 3 prioridades de amanhã',
      '规划明天最重要的3件事',
    ),
    icon: 'list-checks',
    category: 'career',
    period: weekday,
  },
  practiceSkill: {
    title: l(
      'Practice your skill for 30 minutes',
      'Practicar tu habilidad 30 minutos',
      'Pratiquer sa compétence 30 minutes',
      'スキルを30分練習する',
      'Praticar sua habilidade por 30 minutos',
      '练习技能30分钟',
    ),
    icon: 'target',
    category: 'career',
    period: daily,
  },
  learnedNote: {
    title: l(
      'Write down one thing you learned',
      'Anotar algo que aprendiste',
      'Noter une chose apprise',
      '学んだことを1つ書く',
      'Anotar uma coisa que aprendeu',
      '记下学到的一件事',
    ),
    icon: 'lightbulb',
    category: 'career',
    period: daily,
  },
  sideProject: {
    title: l(
      'Work on your side project',
      'Avanzar en tu proyecto personal',
      'Avancer sur son projet perso',
      'サイドプロジェクトを進める',
      'Avançar no projeto paralelo',
      '推进副业项目',
    ),
    icon: 'hammer',
    category: 'career',
    period: 'days',
    days: [2, 4, 6],
  },
  shipWeekly: {
    title: l(
      'Ship one small thing',
      'Publicar una pequeña mejora',
      'Livrer une petite chose',
      '小さな成果を1つ出す',
      'Entregar uma pequena coisa',
      '交付一个小成果',
    ),
    icon: 'trophy',
    category: 'career',
    period: weekly,
  },
  reachOut: {
    title: l(
      'Reach out to someone in your field',
      'Contactar a alguien de tu sector',
      'Contacter quelqu’un de son domaine',
      '同じ業界の人に連絡する',
      'Falar com alguém da sua área',
      '联系一位同行',
    ),
    icon: 'message-circle',
    category: 'career',
    period: weekly,
  },
  coffeeChat: {
    title: l(
      'Have a coffee chat',
      'Tomar un café con un contacto',
      'Prendre un café avec un contact',
      'コーヒーを飲みながら情報交換',
      'Tomar um café com um contato',
      '约人喝咖啡聊聊',
    ),
    icon: 'coffee',
    category: 'career',
    period: monthly,
  },
  weeklyWins: {
    title: l(
      'Write down this week’s wins',
      'Anotar los logros de la semana',
      'Noter les réussites de la semaine',
      '今週の成果を書き出す',
      'Anotar as conquistas da semana',
      '记录本周成果',
    ),
    icon: 'trophy',
    category: 'career',
    period: weekly,
  },
  // mind
  meditate: {
    title: l(
      'Meditate for 10 minutes',
      'Meditar 10 minutos',
      'Méditer 10 minutes',
      '10分瞑想する',
      'Meditar por 10 minutos',
      '冥想10分钟',
    ),
    icon: 'leaf',
    category: 'mind',
    period: daily,
  },
  breaths: {
    title: l(
      'Three deep breaths before a stressful task',
      'Tres respiraciones profundas antes de una tarea estresante',
      'Trois grandes respirations avant une tâche stressante',
      'ストレスのかかる作業の前に深呼吸を3回',
      'Três respirações profundas antes de uma tarefa estressante',
      '做有压力的事前深呼吸三次',
    ),
    icon: 'wind',
    category: 'mind',
    period: daily,
  },
  journal: {
    title: l(
      'Journal for 5 minutes',
      'Escribir en tu diario 5 minutos',
      'Écrire 5 minutes dans son journal',
      '5分ジャーナリング',
      'Escrever no diário por 5 minutos',
      '写5分钟日记',
    ),
    icon: 'pen-line',
    category: 'mind',
    period: daily,
  },
  dayWin: {
    title: l(
      'Note one win from today',
      'Anotar un logro del día',
      'Noter une victoire du jour',
      '今日のよかったことを1つ書く',
      'Anotar uma vitória do dia',
      '记下今天的一个小成就',
    ),
    icon: 'trophy',
    category: 'mind',
    period: daily,
  },
  phoneFreeMorning: {
    title: l(
      'First hour of the day phone-free',
      'Primera hora del día sin móvil',
      'Première heure de la journée sans téléphone',
      '朝の1時間はスマホなし',
      'Primeira hora do dia sem celular',
      '早上第一个小时不碰手机',
    ),
    icon: 'phone-off',
    category: 'mind',
    period: daily,
  },
  screenTime: {
    title: l(
      'Screen time',
      'Tiempo de pantalla',
      'Temps d’écran',
      'スクリーンタイム',
      'Tempo de tela',
      '屏幕时间',
    ),
    icon: 'phone-off',
    category: 'mind',
    period: daily,
    measure: {
      custom: { direction: 'atMost', target: 120, quickAdds: [15, 30, 60] },
      unit: l('min', 'min', 'min', '分', 'min', '分钟'),
    },
  },
  gratitude3: {
    title: l(
      'Write three things you’re grateful for',
      'Escribir tres cosas que agradeces',
      'Noter trois choses dont on est reconnaissant',
      '感謝していることを3つ書く',
      'Escrever três coisas pelas quais você é grato',
      '写下三件感恩的事',
    ),
    icon: 'heart',
    category: 'mind',
    period: daily,
  },
  thankSomeone: {
    title: l(
      'Thank someone',
      'Dar las gracias a alguien',
      'Remercier quelqu’un',
      '誰かに感謝を伝える',
      'Agradecer a alguém',
      '向一个人道谢',
    ),
    icon: 'hand-heart',
    category: 'mind',
    period: daily,
  },
  pages: {
    title: l('Read', 'Leer', 'Lire', '読書', 'Ler', '阅读'),
    icon: 'book-open',
    category: 'mind',
    period: daily,
    measure: { preset: 'pages' },
  },
  readBed: {
    title: l(
      'Read before bed',
      'Leer antes de dormir',
      'Lire avant de dormir',
      '寝る前に読書',
      'Ler antes de dormir',
      '睡前阅读',
    ),
    icon: 'book-open',
    category: 'mind',
    period: daily,
  },
  // money
  noSpend: {
    title: l(
      'No-spend day',
      'Día sin gastos',
      'Journée sans dépense',
      'お金を使わない日',
      'Dia sem gastos',
      '零消费日',
    ),
    icon: 'piggy-bank',
    category: 'finance',
    period: weekly,
  },
  saveTransfer: {
    title: l(
      'Move money into savings',
      'Pasar dinero al ahorro',
      'Virer de l’argent sur l’épargne',
      '貯金口座にお金を移す',
      'Transferir dinheiro para a poupança',
      '转一笔钱到储蓄',
    ),
    icon: 'piggy-bank',
    category: 'finance',
    period: weekly,
  },
  logSpend: {
    title: l(
      'Log today’s spending',
      'Anotar los gastos del día',
      'Noter les dépenses du jour',
      '今日の支出を記録する',
      'Registrar os gastos do dia',
      '记录今天的开销',
    ),
    icon: 'receipt',
    category: 'finance',
    period: daily,
  },
  budgetReview: {
    title: l(
      'Review your budget',
      'Revisar tu presupuesto',
      'Faire le point sur son budget',
      '予算を見直す',
      'Revisar seu orçamento',
      '回顾预算',
    ),
    icon: 'wallet',
    category: 'finance',
    period: weekly,
  },
  invest: {
    title: l(
      'Invest a set amount',
      'Invertir una cantidad fija',
      'Investir un montant fixe',
      '決まった額を投資する',
      'Investir um valor fixo',
      '定额投资',
    ),
    icon: 'trending-up',
    category: 'finance',
    period: monthly,
  },
  learnInvesting: {
    title: l(
      'Read about investing for 15 minutes',
      'Leer sobre inversión 15 minutos',
      'Lire 15 minutes sur l’investissement',
      '投資について15分学ぶ',
      'Ler sobre investimentos por 15 minutos',
      '花15分钟学习投资',
    ),
    icon: 'book-open',
    category: 'finance',
    period: weekly,
  },
  extraPayment: {
    title: l(
      'Make an extra debt payment',
      'Hacer un pago extra de la deuda',
      'Faire un remboursement supplémentaire',
      '繰り上げ返済する',
      'Fazer um pagamento extra da dívida',
      '额外还一笔债',
    ),
    icon: 'coins',
    category: 'finance',
    period: monthly,
  },
  // relationships
  familyMeal: {
    title: l(
      'Device-free family meal',
      'Comida en familia sin pantallas',
      'Repas en famille sans écrans',
      'スマホなしで家族と食事',
      'Refeição em família sem telas',
      '不看手机的家庭用餐',
    ),
    icon: 'utensils',
    category: 'relationships',
    period: daily,
  },
  familyOuting: {
    title: l(
      'Family activity',
      'Actividad en familia',
      'Activité en famille',
      '家族で何かをする',
      'Atividade em família',
      '家庭活动',
    ),
    icon: 'users',
    category: 'relationships',
    period: weekly,
  },
  dateNight: {
    title: l(
      'Date night',
      'Noche en pareja',
      'Soirée en amoureux',
      'デートの夜',
      'Noite a dois',
      '约会之夜',
    ),
    icon: 'heart',
    category: 'relationships',
    period: weekly,
  },
  appreciatePartner: {
    title: l(
      'Tell your partner one thing you appreciate',
      'Decirle a tu pareja algo que valoras',
      'Dire à son/sa partenaire une chose qu’on apprécie',
      'パートナーに感謝を1つ伝える',
      'Dizer ao seu par algo que você valoriza',
      '告诉伴侣一件你欣赏的事',
    ),
    icon: 'heart',
    category: 'relationships',
    period: daily,
  },
  messageFriend: {
    title: l(
      'Message a friend',
      'Escribir a un amigo',
      'Écrire à un ami',
      '友達に連絡する',
      'Mandar mensagem a um amigo',
      '给朋友发消息',
    ),
    icon: 'message-circle',
    category: 'relationships',
    period: weekly,
  },
  seeFriends: {
    title: l(
      'Meet up with friends',
      'Quedar con amigos',
      'Voir des amis',
      '友達と会う',
      'Encontrar os amigos',
      '和朋友见面',
    ),
    icon: 'users',
    category: 'relationships',
    period: monthly,
  },
  callParents: {
    title: l(
      'Call your parents',
      'Llamar a tus padres',
      'Appeler ses parents',
      '親に電話する',
      'Ligar para seus pais',
      '给父母打电话',
    ),
    icon: 'phone',
    category: 'relationships',
    period: weekly,
  },
  sendUpdate: {
    title: l(
      'Send your parents a photo or update',
      'Mandar a tus padres una foto o novedad',
      'Envoyer une photo ou des nouvelles à ses parents',
      '親に写真や近況を送る',
      'Mandar uma foto ou novidade para seus pais',
      '给父母发张照片或近况',
    ),
    icon: 'camera',
    category: 'relationships',
    period: weekly,
  },
  // creativity
  words: {
    title: l(
      'Words written',
      'Palabras escritas',
      'Mots écrits',
      '執筆文字数',
      'Palavras escritas',
      '写作字数',
    ),
    icon: 'pencil',
    category: 'creative',
    period: daily,
    measure: {
      custom: { direction: 'atLeast', target: 300, quickAdds: [100, 250] },
      unit: l('words', 'palabras', 'mots', '字', 'palavras', '字'),
    },
  },
  captureIdea: {
    title: l(
      'Capture one idea',
      'Apuntar una idea',
      'Noter une idée',
      'アイデアを1つメモする',
      'Anotar uma ideia',
      '记下一个点子',
    ),
    icon: 'lightbulb',
    category: 'creative',
    period: daily,
  },
  instrument: {
    title: l(
      'Practice your instrument for 20 minutes',
      'Practicar tu instrumento 20 minutos',
      'Jouer de son instrument 20 minutes',
      '楽器を20分練習する',
      'Praticar seu instrumento por 20 minutos',
      '练习乐器20分钟',
    ),
    icon: 'guitar',
    category: 'creative',
    period: daily,
  },
  newSong: {
    title: l(
      'Learn part of a new song',
      'Aprender parte de una canción nueva',
      'Apprendre un bout d’un nouveau morceau',
      '新しい曲を少し覚える',
      'Aprender parte de uma música nova',
      '学一段新曲子',
    ),
    icon: 'music',
    category: 'creative',
    period: weekly,
  },
  sketch: {
    title: l(
      'Sketch for 15 minutes',
      'Dibujar 15 minutos',
      'Dessiner 15 minutes',
      '15分スケッチする',
      'Desenhar por 15 minutos',
      '画15分钟速写',
    ),
    icon: 'palette',
    category: 'creative',
    period: daily,
  },
  finishPiece: {
    title: l(
      'Finish one small piece',
      'Terminar una obra pequeña',
      'Terminer une petite œuvre',
      '小さな作品を1つ仕上げる',
      'Terminar uma pequena obra',
      '完成一件小作品',
    ),
    icon: 'star',
    category: 'creative',
    period: weekly,
  },
  createContent: {
    title: l(
      'Create one piece of content',
      'Crear una pieza de contenido',
      'Créer un contenu',
      'コンテンツを1本作る',
      'Criar um conteúdo',
      '创作一条内容',
    ),
    icon: 'camera',
    category: 'creative',
    period: 'days',
    days: [1, 3, 5],
  },
  // learning
  languagePractice: {
    title: l(
      'Practice your language for 15 minutes',
      'Practicar el idioma 15 minutos',
      'Pratiquer la langue 15 minutes',
      '語学を15分勉強する',
      'Praticar o idioma por 15 minutos',
      '练习外语15分钟',
    ),
    icon: 'languages',
    category: 'career',
    period: daily,
  },
  newWords: {
    title: l(
      'New words',
      'Palabras nuevas',
      'Mots nouveaux',
      '新しい単語',
      'Palavras novas',
      '新单词',
    ),
    icon: 'languages',
    category: 'career',
    period: daily,
    measure: {
      custom: { direction: 'atLeast', target: 10, quickAdds: [5] },
      unit: l('words', 'palabras', 'mots', '語', 'palavras', '个'),
    },
  },
  lesson: {
    title: l(
      'Do one lesson of your course',
      'Hacer una lección de tu curso',
      'Faire une leçon de son cours',
      '講座を1レッスン進める',
      'Fazer uma aula do seu curso',
      '完成一节课程',
    ),
    icon: 'graduation-cap',
    category: 'career',
    period: weekday,
  },
} satisfies Record<string, ImproveSuggestion>

export type SuggestionKey = keyof typeof IMPROVE_SUGGESTIONS

// ─── areas ───────────────────────────────────────────────────────────────────

export type ImproveSubArea = {
  key: string
  label: L10n
  // Best first; the top `picks` (default 2) are pre-ticked.
  suggestions: SuggestionKey[]
  picks?: number
}

export type ImproveArea = {
  key: string
  category: LifeCategory
  icon: string
  label: L10n
  subAreas: ImproveSubArea[]
}

export const IMPROVE_AREAS: ImproveArea[] = [
  {
    key: 'health',
    category: 'health',
    icon: 'heart-pulse',
    label: l(
      'Health & fitness',
      'Salud y forma física',
      'Santé et forme',
      '健康・フィットネス',
      'Saúde e forma física',
      '健康与健身',
    ),
    subAreas: [
      {
        key: 'stamina',
        label: l(
          'Stamina & cardio',
          'Resistencia y cardio',
          'Endurance et cardio',
          'スタミナ・有酸素運動',
          'Resistência e cardio',
          '耐力与有氧',
        ),
        suggestions: ['jog', 'steps', 'stairs'],
      },
      {
        key: 'muscle',
        label: l(
          'Build muscle',
          'Ganar músculo',
          'Prendre du muscle',
          '筋肉をつける',
          'Ganhar músculo',
          '增肌',
        ),
        suggestions: ['lift', 'meals', 'protein', 'sleep8'],
      },
      {
        key: 'loseWeight',
        label: l(
          'Lose weight',
          'Perder peso',
          'Perdre du poids',
          '体重を落とす',
          'Perder peso',
          '减重',
        ),
        suggestions: ['steps', 'water', 'noLateSnacks', 'veggies'],
      },
      {
        key: 'sleep',
        label: l(
          'Sleep better',
          'Dormir mejor',
          'Mieux dormir',
          'よく眠る',
          'Dormir melhor',
          '睡得更好',
        ),
        suggestions: ['sleep8', 'bedtime', 'noScreensBed'],
      },
      {
        key: 'eatBetter',
        label: l(
          'Eat better',
          'Comer mejor',
          'Mieux manger',
          '食生活を整える',
          'Comer melhor',
          '吃得更健康',
        ),
        suggestions: ['veggies', 'water', 'cookHome'],
      },
      {
        key: 'flexibility',
        label: l('Flexibility', 'Flexibilidad', 'Souplesse', '柔軟性', 'Flexibilidade', '柔韧性'),
        suggestions: ['stretch', 'yoga'],
      },
      {
        key: 'quitSmoking',
        label: l(
          'Quit smoking',
          'Dejar de fumar',
          'Arrêter de fumer',
          '禁煙',
          'Parar de fumar',
          '戒烟',
        ),
        suggestions: ['smokeFree', 'cravingBreaths'],
        picks: 1,
      },
      {
        key: 'lessAlcohol',
        label: l(
          'Drink less alcohol',
          'Beber menos alcohol',
          'Boire moins d’alcool',
          'お酒を減らす',
          'Beber menos álcool',
          '少喝酒',
        ),
        suggestions: ['dryWeekdays', 'alcoholFree'],
        picks: 1,
      },
      {
        key: 'lessSugar',
        label: l(
          'Cut sugar',
          'Reducir el azúcar',
          'Réduire le sucre',
          '砂糖を減らす',
          'Cortar o açúcar',
          '控糖',
        ),
        suggestions: ['noSugaryDrinks', 'noAddedSugar'],
        picks: 1,
      },
      {
        key: 'fasting',
        label: l(
          'Intermittent fasting',
          'Ayuno intermitente',
          'Jeûne intermittent',
          '断続的断食',
          'Jejum intermitente',
          '间歇性断食',
        ),
        suggestions: ['fast16', 'water'],
        picks: 1,
      },
    ],
  },
  {
    key: 'career',
    category: 'career',
    icon: 'briefcase',
    label: l(
      'Career & work',
      'Carrera y trabajo',
      'Carrière et travail',
      'キャリア・仕事',
      'Carreira e trabalho',
      '事业与工作',
    ),
    subAreas: [
      {
        key: 'deepWork',
        label: l(
          'Deep work & focus',
          'Trabajo profundo y foco',
          'Concentration au travail',
          '集中して働く',
          'Trabalho focado',
          '深度工作与专注',
        ),
        suggestions: ['deepWork', 'phoneAway', 'top3'],
      },
      {
        key: 'skill',
        label: l(
          'Learn a skill',
          'Aprender una habilidad',
          'Apprendre une compétence',
          'スキルを身につける',
          'Aprender uma habilidade',
          '学一项技能',
        ),
        suggestions: ['practiceSkill', 'learnedNote'],
      },
      {
        key: 'sideProject',
        label: l(
          'Side project',
          'Proyecto personal',
          'Projet perso',
          'サイドプロジェクト',
          'Projeto paralelo',
          '副业项目',
        ),
        suggestions: ['sideProject', 'shipWeekly'],
      },
      {
        key: 'network',
        label: l(
          'Grow my network',
          'Ampliar mi red',
          'Développer mon réseau',
          '人脈を広げる',
          'Ampliar minha rede',
          '拓展人脉',
        ),
        suggestions: ['reachOut', 'coffeeChat'],
      },
      {
        key: 'promotion',
        label: l(
          'Get promoted',
          'Conseguir un ascenso',
          'Obtenir une promotion',
          '昇進する',
          'Ser promovido',
          '升职',
        ),
        suggestions: ['top3', 'weeklyWins', 'deepWork'],
      },
    ],
  },
  {
    key: 'mind',
    category: 'mind',
    icon: 'brain',
    label: l(
      'Mind & wellbeing',
      'Mente y bienestar',
      'Esprit et bien-être',
      '心と幸福感',
      'Mente e bem-estar',
      '心灵与身心健康',
    ),
    subAreas: [
      {
        key: 'meditation',
        label: l('Meditation', 'Meditación', 'Méditation', '瞑想', 'Meditação', '冥想'),
        suggestions: ['meditate', 'breaths'],
      },
      {
        key: 'journaling',
        label: l(
          'Journaling',
          'Escribir un diario',
          'Tenir un journal',
          'ジャーナリング',
          'Diário',
          '写日记',
        ),
        suggestions: ['journal', 'dayWin'],
      },
      {
        key: 'screenTime',
        label: l(
          'Less screen time',
          'Menos pantallas',
          'Moins d’écrans',
          'スマホ時間を減らす',
          'Menos tempo de tela',
          '减少屏幕时间',
        ),
        suggestions: ['phoneFreeMorning', 'noScreensBed', 'screenTime'],
      },
      {
        key: 'gratitude',
        label: l('Gratitude', 'Gratitud', 'Gratitude', '感謝', 'Gratidão', '感恩'),
        suggestions: ['gratitude3', 'thankSomeone'],
      },
      {
        key: 'reading',
        label: l('Read more', 'Leer más', 'Lire plus', 'もっと本を読む', 'Ler mais', '多读书'),
        suggestions: ['pages', 'readBed'],
        picks: 1,
      },
    ],
  },
  {
    key: 'money',
    category: 'finance',
    icon: 'wallet',
    label: l('Money', 'Dinero', 'Argent', 'お金', 'Dinheiro', '理财'),
    subAreas: [
      {
        key: 'save',
        label: l(
          'Save more',
          'Ahorrar más',
          'Épargner plus',
          'もっと貯金する',
          'Poupar mais',
          '多存钱',
        ),
        suggestions: ['noSpend', 'saveTransfer'],
      },
      {
        key: 'budget',
        label: l(
          'Budget',
          'Hacer presupuesto',
          'Tenir un budget',
          '予算管理',
          'Fazer orçamento',
          '做预算',
        ),
        suggestions: ['logSpend', 'budgetReview'],
      },
      {
        key: 'invest',
        label: l('Invest', 'Invertir', 'Investir', '投資する', 'Investir', '投资'),
        suggestions: ['invest', 'learnInvesting'],
      },
      {
        key: 'debt',
        label: l(
          'Pay off debt',
          'Pagar deudas',
          'Rembourser mes dettes',
          '借金を返す',
          'Quitar dívidas',
          '还清债务',
        ),
        suggestions: ['extraPayment', 'logSpend'],
      },
    ],
  },
  {
    key: 'relationships',
    category: 'relationships',
    icon: 'heart',
    label: l('Relationships', 'Relaciones', 'Relations', '人間関係', 'Relacionamentos', '人际关系'),
    subAreas: [
      {
        key: 'family',
        label: l(
          'Family time',
          'Tiempo en familia',
          'Temps en famille',
          '家族との時間',
          'Tempo em família',
          '陪伴家人',
        ),
        suggestions: ['familyMeal', 'familyOuting'],
      },
      {
        key: 'partner',
        label: l('My partner', 'Mi pareja', 'Mon couple', 'パートナー', 'Vida a dois', '伴侣'),
        suggestions: ['dateNight', 'appreciatePartner'],
      },
      {
        key: 'friends',
        label: l('Friends', 'Amigos', 'Amis', '友人', 'Amigos', '朋友'),
        suggestions: ['messageFriend', 'seeFriends'],
      },
      {
        key: 'parents',
        label: l(
          'Call my parents',
          'Llamar a mis padres',
          'Appeler mes parents',
          '親に連絡する',
          'Ligar para meus pais',
          '给父母打电话',
        ),
        suggestions: ['callParents', 'sendUpdate'],
        picks: 1,
      },
    ],
  },
  {
    key: 'creativity',
    category: 'creative',
    icon: 'palette',
    label: l('Creativity', 'Creatividad', 'Créativité', '創造性', 'Criatividade', '创造力'),
    subAreas: [
      {
        key: 'writing',
        label: l('Write', 'Escribir', 'Écrire', '書く', 'Escrever', '写作'),
        suggestions: ['words', 'captureIdea'],
      },
      {
        key: 'music',
        label: l('Music', 'Música', 'Musique', '音楽', 'Música', '音乐'),
        suggestions: ['instrument', 'newSong'],
      },
      {
        key: 'art',
        label: l(
          'Art & drawing',
          'Arte y dibujo',
          'Art et dessin',
          'アート・絵',
          'Arte e desenho',
          '艺术与绘画',
        ),
        suggestions: ['sketch', 'finishPiece'],
      },
      {
        key: 'content',
        label: l(
          'Create content',
          'Crear contenido',
          'Créer du contenu',
          'コンテンツ制作',
          'Criar conteúdo',
          '内容创作',
        ),
        suggestions: ['createContent', 'captureIdea'],
      },
    ],
  },
  {
    key: 'learning',
    category: 'career',
    icon: 'graduation-cap',
    label: l('Learning', 'Aprendizaje', 'Apprentissage', '学び', 'Aprendizado', '学习'),
    subAreas: [
      {
        key: 'languages',
        label: l('Languages', 'Idiomas', 'Langues', '語学', 'Idiomas', '语言'),
        suggestions: ['languagePractice', 'newWords'],
      },
      {
        key: 'courses',
        label: l('Courses', 'Cursos', 'Cours', '講座', 'Cursos', '课程'),
        suggestions: ['lesson', 'learnedNote'],
      },
      {
        key: 'books',
        label: l('Books', 'Libros', 'Livres', '本', 'Livros', '读书'),
        suggestions: ['pages', 'readBed'],
        picks: 1,
      },
    ],
  },
]

// ─── helpers ─────────────────────────────────────────────────────────────────

const SUB_AREAS = new Map(
  IMPROVE_AREAS.flatMap((a) => a.subAreas.map((s) => [s.key, { ...s, area: a.key }] as const)),
)

export const MAX_IMPROVE_PICKS = 5
export const DEFAULT_SUBAREA_PICKS = 2

export function improveArea(key: string): ImproveArea | undefined {
  return IMPROVE_AREAS.find((a) => a.key === key)
}

export function improveSubArea(key: string) {
  return SUB_AREAS.get(key)
}

// The area a goal category falls under, for pre-selecting it.
export function areaForCategory(category: string | null | undefined): string | undefined {
  const c = category?.toLowerCase()
  return IMPROVE_AREAS.find((a) => a.category === c)?.key
}

// Known keys only, in catalogue order; sub-areas need their area picked.
export function cleanImproveAreas(
  areas: readonly string[],
  subAreas: readonly string[],
): { areas: string[]; subAreas: string[] } {
  const a = IMPROVE_AREAS.filter((x) => areas.includes(x.key))
  return {
    areas: a.map((x) => x.key),
    subAreas: a.flatMap((x) =>
      x.subAreas.filter((s) => subAreas.includes(s.key)).map((s) => s.key),
    ),
  }
}

// Suggestions grouped by sub-area, in the order picked; a suggestion shared by
// two sub-areas shows under the first only.
export function suggestionGroups(
  subAreas: readonly string[],
): { subArea: string; area: string; suggestions: SuggestionKey[] }[] {
  const seen = new Set<SuggestionKey>()
  return subAreas.flatMap((key) => {
    const s = SUB_AREAS.get(key)
    if (!s) return []
    const fresh = s.suggestions.filter((k) => !seen.has(k) && seen.add(k))
    return fresh.length ? [{ subArea: key, area: s.area, suggestions: fresh }] : []
  })
}

// Pre-ticked suggestions: round-robin over the picked sub-areas (each one's
// best first, up to its `picks`), capped so a new user starts small.
export function defaultSuggestionPicks(
  subAreas: readonly string[],
  cap = MAX_IMPROVE_PICKS,
): SuggestionKey[] {
  const picks: SuggestionKey[] = []
  const subs = subAreas.flatMap((k) => SUB_AREAS.get(k) ?? [])
  const rounds = Math.max(0, ...subs.map((s) => s.picks ?? DEFAULT_SUBAREA_PICKS))
  for (let r = 0; r < rounds; r++) {
    for (const s of subs) {
      if (picks.length >= cap) return picks
      const k = s.suggestions[r]
      if (r < (s.picks ?? DEFAULT_SUBAREA_PICKS) && k && !picks.includes(k)) picks.push(k)
    }
  }
  return picks
}

export function suggestionMeasure(key: SuggestionKey, lang: ImproveLang): HabitMeasure | undefined {
  const m: SuggestionMeasure | undefined = (IMPROVE_SUGGESTIONS[key] as ImproveSuggestion).measure
  if (!m) return undefined
  if ('fast' in m) {
    const f = FAST_PRESETS.find((p) => p.id === m.fast)!
    return fastMeasure(f.hours, FAST_UNIT[lang])
  }
  if ('preset' in m) {
    const p = MEASURE_PRESETS.find((x) => x.id === m.preset)!
    return { ...p.measure, unit: PRESET_UNITS[m.preset]?.[lang] ?? p.measure.unit }
  }
  return { ...m.custom, unit: m.unit[lang] }
}

// addHabits args for a suggestion, in the user's language.
export function suggestionHabit(key: SuggestionKey, lang: ImproveLang) {
  const s: ImproveSuggestion = IMPROVE_SUGGESTIONS[key]
  const measure = suggestionMeasure(key, lang)
  return {
    title: s.title[lang],
    category: s.category,
    icon: s.icon,
    recursPeriod: s.period,
    ...(s.days && { recursDays: s.days }),
    ...(measure && { measure }),
  }
}

// One line for the coach: "Wants to improve: Build muscle, Sleep better (Health & fitness); Save more (Money)".
export function improveAreasLine(areas: readonly string[], subAreas: readonly string[]): string {
  const parts = IMPROVE_AREAS.filter((a) => areas.includes(a.key)).map((a) => {
    const subs = a.subAreas.filter((s) => subAreas.includes(s.key)).map((s) => s.label.en)
    return subs.length ? `${subs.join(', ')} (${a.label.en})` : a.label.en
  })
  return parts.length ? `Wants to improve: ${parts.join('; ')}` : ''
}

// "≥ 5 meals", "≤ 120 min", "16 h" for a suggestion's row.
export function measureLabel(m: HabitMeasure, locale: string): string {
  const n = m.target.toLocaleString(locale)
  return m.fast ? `${n} ${m.unit}` : `${m.direction === 'atMost' ? '≤' : '≥'} ${n} ${m.unit}`
}
